import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    CircularProgress,
    TextField,
    Grid,
    LinearProgress,
    Autocomplete,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { analysisService } from "../../../shared/services/api/analysis/analysisService";
import { clientService, IListagemCliente } from "../../../shared/services/api/client/clientService";
import { useToast } from "../../../shared/hooks";
import { ToastContent } from "../../../shared/components";
import { format, subDays } from "date-fns";
import { globalMaskDocument } from "../../../shared/utils/globalMask-document";
import { AnalysisStatusChip } from "./AnalysisStatusChip";

interface IAnalysisBatchStatusModalProps {
    open: boolean;
    onClose: () => void;
}

export const AnalysisBatchStatusModal: React.FC<IAnalysisBatchStatusModalProps> = ({ open, onClose }) => {
    const [analyses, setAnalyses] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [limit, setLimit] = useState(10);

    // Filters
    const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
    const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [statusFilter, setStatusFilter] = useState("");
    const [clientFilter, setClientFilter] = useState<string | null>(null);

    // Autocomplete state
    const [clientOptions, setClientOptions] = useState<{ id: string; label: string }[]>([]);
    const [clientSearch, setClientSearch] = useState("");
    const [loadingClients, setLoadingClients] = useState(false);

    // Batch Update State
    const [newStatus, setNewStatus] = useState("");
    const [newObs, setNewObs] = useState("");

    // Processing state
    const [isProcessing, setIsProcessing] = useState(false);
    const [processingStatus, setProcessingStatus] = useState("");
    const [currentProcessingId, setCurrentProcessingId] = useState<string | null>(null);
    const [processedIds, setProcessedIds] = useState<string[]>([]);
    const [processSummary, setProcessSummary] = useState<{ total: number; success: number; error: number } | null>(null);

    const toastHandler = useToast();

    // Fetch Clients for Autocomplete
    useEffect(() => {
        if (open) {
            setLoadingClients(true);
            clientService.getSelect(1, clientSearch, "")
                .then((result) => {
                    if (result instanceof Error) {
                        console.error(result.message);
                    } else {
                        const options = result.data.map((client: IListagemCliente) => ({
                            id: client.id,
                            label: client.name
                        }));
                        setClientOptions(options);
                    }
                })
                .finally(() => setLoadingClients(false));
        }
    }, [open, clientSearch]);

    const fetchAnalyses = async (
        start = startDate,
        end = endDate,
        lim = limit,
        status = statusFilter,
        clientId = clientFilter || ""
    ) => {
        setLoading(true);
        try {
            const result = await analysisService.getAll(
                1,
                "",
                "",
                "",
                status,
                start,
                end,
                clientId,
                "",
                lim
            );

            if (result instanceof Error) {
                toastHandler.present({
                    type: "error",
                    messageOrContent: <ToastContent title="Erro" content={result.message} />
                });
            } else {
                setAnalyses(result.data);
            }
        } catch (error) {
            console.error("Fetch error:", error);
        } finally {
            setLoading(false);
        }
    };

    // Effect for opening (Reset and Initial Fetch)
    useEffect(() => {
        if (open) {
            const defaultStart = format(subDays(new Date(), 30), 'yyyy-MM-dd');
            const defaultEnd = format(new Date(), 'yyyy-MM-dd');

            setStartDate(defaultStart);
            setEndDate(defaultEnd);
            setStatusFilter("");
            setClientFilter(null);
            setClientSearch("");

            setSelectedIds([]);
            setLimit(10);
            setProcessedIds([]);
            setIsProcessing(false);
            setProcessingStatus("");
            setCurrentProcessingId(null);
            setProcessSummary(null);
            setNewStatus("");
            setNewObs("");

            fetchAnalyses(defaultStart, defaultEnd, 10, "", "");
        }
    }, [open]);

    // Effect for pagination (Load More)
    useEffect(() => {
        if (open && limit > 10) {
            fetchAnalyses();
        }
    }, [limit]);

    const handleFilter = () => {
        setLimit(10);
        setProcessedIds([]);
        setSelectedIds([]);
        setProcessSummary(null);
        fetchAnalyses(startDate, endDate, 10, statusFilter, clientFilter || "");
    };

    const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelecteds = analyses
                .filter(n => !processedIds.includes(n.id))
                .map((n) => n.id);
            setSelectedIds(newSelecteds);
        } else {
            setSelectedIds([]);
        }
    };

    const handleClick = (event: React.MouseEvent<unknown>, id: string) => {
        if (isProcessing) return;

        const selectedIndex = selectedIds.indexOf(id);
        let newSelected: string[] = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selectedIds, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selectedIds.slice(1));
        } else if (selectedIndex === selectedIds.length - 1) {
            newSelected = newSelected.concat(selectedIds.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selectedIds.slice(0, selectedIndex),
                selectedIds.slice(selectedIndex + 1),
            );
        }
        setSelectedIds(newSelected);
    };

    const handleBatchUpdate = async () => {
        if (!newStatus) {
            toastHandler.present({
                type: "warning",
                messageOrContent: <ToastContent title="Atenção" content="Selecione um novo status." />
            });
            return;
        }
        if (!newObs) {
            toastHandler.present({
                type: "warning",
                messageOrContent: <ToastContent title="Atenção" content="Informe o motivo da alteração." />
            });
            return;
        }

        setIsProcessing(true);
        setProcessedIds([]);
        let successCount = 0;
        let errorCount = 0;
        const total = selectedIds.length;

        for (let i = 0; i < total; i++) {
            const id = selectedIds[i];
            const analysis = analyses.find(a => a.id === id);

            if (analysis) {
                setCurrentProcessingId(id);
                setProcessingStatus(`Atualizando ${i + 1} de ${total}...`);

                if (i > 0) {
                    await new Promise(resolve => setTimeout(resolve, 500)); // Small delay to avoid rate limiting
                }

                try {
                    const currentObs = analysis.obs || "";
                    const updatedObs = currentObs ? `${currentObs} | ${newObs}` : newObs;

                    const payload = {
                        status: newStatus,
                        obs: updatedObs,
                    };

                    const result = await analysisService.updateGeneric(id, payload);

                    if (result instanceof Error) {
                        errorCount++;
                    } else {
                        successCount++;
                    }
                } catch (e) {
                    errorCount++;
                }

                setProcessedIds(prev => [...prev, id]);
            }
        }

        setIsProcessing(false);
        setCurrentProcessingId(null);
        setProcessingStatus("");
        setProcessSummary({
            total: total,
            success: successCount,
            error: errorCount
        });

        toastHandler.present({
            type: successCount > 0 ? "success" : "error",
            messageOrContent: <ToastContent
                title="Processamento Concluído"
                content={`Sucesso: ${successCount}, Erros: ${errorCount}`}
            />
        });

        setSelectedIds([]);
        // Refresh list to show new statuses? Or just keep them as processed?
        // User requested "disable auto-refresh" for resend, probably same here.
    };

    const handleLoadMore = () => {
        setLimit(prev => prev + 10);
    };

    return (
        <Dialog open={open} onClose={isProcessing ? undefined : onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                Alterar Status em Lote {analyses.length > 0 && `(${analyses.length})`}
                {isProcessing && (
                    <Box mt={1}>
                        <Typography variant="body2" color="primary">
                            {processingStatus}
                        </Typography>
                        <LinearProgress />
                    </Box>
                )}
            </DialogTitle>
            <DialogContent>
                <Box mb={2} mt={1}>
                    {processSummary && (
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                mb: 2,
                                bgcolor: 'grey.100',
                                display: 'flex',
                                justifyContent: 'space-around',
                                alignItems: 'center'
                            }}
                        >
                            <Box textAlign="center">
                                <Typography variant="subtitle2" color="textSecondary">Processadas</Typography>
                                <Typography variant="h6">{processSummary.total}</Typography>
                            </Box>
                            <Box textAlign="center">
                                <Typography variant="subtitle2" color="success.main">Sucesso</Typography>
                                <Typography variant="h6" color="success.main">{processSummary.success}</Typography>
                            </Box>
                            <Box textAlign="center">
                                <Typography variant="subtitle2" color="error.main">Erros</Typography>
                                <Typography variant="h6" color="error.main">{processSummary.error}</Typography>
                            </Box>
                        </Paper>
                    )}

                    {/* Filters */}
                    <Grid container spacing={2} alignItems="center" mb={2}>
                        <Grid item xs={12} sm={4} md={3}>
                            <Autocomplete
                                fullWidth
                                size="small"
                                options={clientOptions}
                                loading={loadingClients}
                                getOptionLabel={(option) => option.label}
                                value={clientOptions.find(c => c.id === clientFilter) || null}
                                onChange={(_, newValue) => setClientFilter(newValue ? newValue.id : null)}
                                onInputChange={(_, newInputValue) => setClientSearch(newInputValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Imobiliária (Filtro)"
                                        disabled={isProcessing}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Status (Filtro)</InputLabel>
                                <Select
                                    value={statusFilter}
                                    label="Status (Filtro)"
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    disabled={isProcessing}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    <MenuItem value="pre_approved">Pré-Aprovada</MenuItem>
                                    <MenuItem value="awaiting_biometria">Pendente biometria</MenuItem>
                                    <MenuItem value="awaiting_signature">Assinatura Pendente</MenuItem>
                                    <MenuItem value="in_hiring">Em contratação</MenuItem>
                                    <MenuItem value="in_progress">Em Andamento</MenuItem>
                                    <MenuItem value="issued">Aprovada</MenuItem>
                                    <MenuItem value="pending">Pendente</MenuItem>
                                    <MenuItem value="expired">Expirada</MenuItem>
                                    <MenuItem value="refused">Recusada</MenuItem>
                                    <MenuItem value="canceled">Cancelada</MenuItem>
                                    <MenuItem value="hired">Contratada</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4} md={2}>
                            <TextField
                                label="Data Inicial"
                                type="date"
                                fullWidth
                                size="small"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                disabled={isProcessing}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4} md={2}>
                            <TextField
                                label="Data Final"
                                type="date"
                                fullWidth
                                size="small"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                disabled={isProcessing}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4} md={2}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleFilter}
                                fullWidth
                                disabled={isProcessing}
                            >
                                Filtrar
                            </Button>
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    {/* Batch Actions */}
                    <Typography variant="subtitle1" gutterBottom>Definir Nova Ação</Typography>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Novo Status</InputLabel>
                                <Select
                                    value={newStatus}
                                    label="Novo Status"
                                    onChange={(e) => setNewStatus(e.target.value)}
                                    disabled={isProcessing}
                                >
                                    <MenuItem value="">Selecione...</MenuItem>
                                    <MenuItem value="pre_approved">Pré-Aprovada</MenuItem>
                                    <MenuItem value="awaiting_biometria">Pendente biometria</MenuItem>
                                    <MenuItem value="awaiting_signature">Assinatura Pendente</MenuItem>
                                    <MenuItem value="in_hiring">Em contratação</MenuItem>
                                    <MenuItem value="in_progress">Em Andamento</MenuItem>
                                    <MenuItem value="issued">Aprovada</MenuItem>
                                    <MenuItem value="pending">Pendente</MenuItem>
                                    <MenuItem value="expired">Expirada</MenuItem>
                                    <MenuItem value="refused">Recusada</MenuItem>
                                    <MenuItem value="canceled">Cancelada</MenuItem>
                                    <MenuItem value="hired">Contratada</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={8} md={6}>
                            <TextField
                                label="Motivo (Observação)"
                                fullWidth
                                size="small"
                                value={newObs}
                                onChange={(e) => setNewObs(e.target.value)}
                                disabled={isProcessing}
                                placeholder="Este texto será adicionado à observação existente"
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={3}>
                            <Button
                                onClick={handleBatchUpdate}
                                color="secondary"
                                variant="contained"
                                fullWidth
                                disabled={selectedIds.length === 0 || loading || isProcessing || !newStatus || !newObs}
                            >
                                {isProcessing ? "Processando..." : `Alterar Status (${selectedIds.length})`}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>

                {loading && analyses.length === 0 ? (
                    <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <TableContainer component={Paper} sx={{ maxHeight: 400, mt: 2 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                indeterminate={selectedIds.length > 0 && selectedIds.length < analyses.length}
                                                checked={analyses.length > 0 && selectedIds.length === analyses.length}
                                                onChange={handleSelectAll}
                                                disabled={isProcessing}
                                            />
                                        </TableCell>
                                        <TableCell>Data</TableCell>
                                        <TableCell>Nome</TableCell>
                                        <TableCell>Documento</TableCell>
                                        <TableCell>Status Atual</TableCell>
                                        <TableCell>ID</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {analyses.map((row) => {
                                        const isItemSelected = selectedIds.indexOf(row.id) !== -1;
                                        const isProcessingItem = currentProcessingId === row.id;
                                        const isProcessed = processedIds.includes(row.id);

                                        return (
                                            <TableRow
                                                hover
                                                onClick={(event) => !isProcessed && !isProcessing && handleClick(event, row.id)}
                                                role="checkbox"
                                                aria-checked={isItemSelected}
                                                tabIndex={-1}
                                                key={row.id}
                                                selected={isItemSelected}
                                                sx={{
                                                    backgroundColor: isProcessingItem ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
                                                    opacity: isProcessed ? 0.6 : 1
                                                }}
                                            >
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={isItemSelected}
                                                        disabled={isProcessing || isProcessed}
                                                    />
                                                </TableCell>
                                                <TableCell>{row.createdAt ? format(new Date(row.createdAt), 'dd/MM/yyyy HH:mm') : '-'}</TableCell>
                                                <TableCell sx={{ fontWeight: isProcessingItem ? 'bold' : 'normal' }}>
                                                    {row.name} {isProcessingItem && "(Atualizando...)"} {isProcessed && "(Concluído)"}
                                                </TableCell>
                                                <TableCell>{globalMaskDocument(row.document)}</TableCell>
                                                <TableCell>
                                                    <AnalysisStatusChip status={row.status} />
                                                </TableCell>
                                                <TableCell>{row.id}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box display="flex" justifyContent="center" mt={2}>
                            <Button onClick={handleLoadMore} disabled={loading || isProcessing}>
                                {loading ? "Carregando..." : "Carregar Mais"}
                            </Button>
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary" disabled={isProcessing}>
                    Fechar
                </Button>
            </DialogActions>
        </Dialog>
    );
};
